import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { jmd } from "@/lib/format";
import { DriverMap } from "@/components/DriverMap";
import { PackageSearch, Loader2, CheckCircle2, Package, Truck, Home, Search } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { k: "confirmed", l: "Confirmed", icon: CheckCircle2 },
  { k: "packed", l: "Packed", icon: Package },
  { k: "out_for_delivery", l: "Out for Delivery", icon: Truck },
  { k: "delivered", l: "Delivered", icon: Home },
];

export default function Track() {
  const [params] = useSearchParams();
  const [orderNo, setOrderNo] = useState(params.get("order") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const track = async (no) => {
    const q = (no ?? orderNo).trim().toUpperCase();
    if (!q) { toast.error("Enter your order number"); return; }
    setLoading(true); setSearched(true);
    try {
      const { data } = await api.get(`/orders/${q}`);
      setOrder(data);
    } catch {
      setOrder(null);
      toast.error("We couldn't find that order number");
    }
    setLoading(false);
  };

  React.useEffect(() => { if (params.get("order")) track(params.get("order")); /* eslint-disable-next-line */ }, []);

  const stages = order?.stages || [];
  const idx = order ? Math.max(...STAGES.map((s, i) => (stages.includes(s.k) ? i : -1)), 0) : 0;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="rounded-2xl sn-ink-bg sn-noise relative overflow-hidden p-7 text-white mb-6">
        <div className="flex items-center gap-3 relative">
          <span className="h-12 w-12 rounded-xl bg-emerald grid place-items-center"><PackageSearch size={24} /></span>
          <div>
            <h1 className="font-display text-2xl font-bold">Track Your Order</h1>
            <p className="text-white/60 text-sm">Live driver location and delivery status — Kingston to the world.</p>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-5 mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && track()}
              placeholder="Enter order number, e.g. SN1A2B3C4D" className="w-full rounded-xl border border-border pl-9 pr-3 py-3 text-sm uppercase" data-testid="track-input" />
          </div>
          <button onClick={() => track()} disabled={loading} className="bg-ink text-white rounded-xl px-6 py-3 text-sm font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="track-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Track
          </button>
        </div>
      </div>

      {order && (
        <div className="bg-card rounded-2xl border border-border p-6 space-y-5" data-testid="track-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div><div className="font-mono-sn font-bold text-lg">{order.order_no}</div><div className="text-xs text-muted-foreground">{order.items?.length || 0} item(s) · {jmd(order.totals?.total || 0)}</div></div>
            <span className="text-xs font-semibold bg-emerald/10 text-emerald px-3 py-1.5 rounded-full capitalize" data-testid="track-status">{(order.status || "").replace(/_/g, " ")}</span>
          </div>
          <div className="flex items-center">
            {STAGES.map((s, i) => (
              <React.Fragment key={s.k}>
                <div className="flex flex-col items-center gap-1">
                  <span className={`h-9 w-9 grid place-items-center rounded-full ${i <= idx ? "bg-emerald text-white" : "bg-secondary text-muted-foreground"}`}><s.icon size={16} /></span>
                  <span className={`text-[10px] ${i <= idx ? "text-ink font-semibold" : "text-muted-foreground"} hidden sm:block`}>{s.l}</span>
                </div>
                {i < STAGES.length - 1 && <div className={`h-0.5 flex-1 ${i < idx ? "bg-emerald" : "bg-border"}`} />}
              </React.Fragment>
            ))}
          </div>
          {(order.status === "out_for_delivery" || order.status === "delivered") ? (
            <DriverMap order={order} />
          ) : (
            <div className="bg-secondary/40 rounded-xl px-4 py-3 text-sm text-muted-foreground">Live map tracking with geofencing appears here the moment your order is out for delivery.</div>
          )}
        </div>
      )}

      {searched && !order && !loading && (
        <div className="bg-card rounded-2xl border border-border p-10 text-center text-muted-foreground" data-testid="track-not-found">
          No order found with that number. Double-check it from your confirmation email or your Account → Orders.
        </div>
      )}
    </div>
  );
}
